/**
 * Monaco (specifically, its language services) uses web workers to compute heavy stuff
 * outside of the UI thread for better performance. Web workers can't load their scripts
 * from a different domain from that which they're run on. That's an issue for us, because
 * we host our JS bundles (including the web worker code) on a CDN at a different domain
 * than our app runs on. Microsoft presents a couple of options here:
 *
 * https://github.com/Microsoft/monaco-editor/blob/14f6229243233f456eb688c0dbdf505c61731b64/docs/integrate-amd-cross.md
 *
 * Using a proxy is not an option for us right now, and the dataURI solution
 * doesn't work correctly in Safari. However, a similar approach using a Blob and creating
 * passing an object URL created from that Blob to the web worker works across all of our
 * supported browsers. More information on the Blob approach can be found here:
 *
 * https://benohead.com/cross-domain-cross-browser-web-workers/
 *
 * The final piece here is `importScripts`, which is part of the web worker API and allows an
 * already running worker to load and run the contents of a script hosted on another domain.
 */

const buildWorker = (url: URL) => {
  return new Worker(
    URL.createObjectURL(
      // @ts-ignore TODO: fix noImplicitAny error here
      new Blob([`importScripts('${url}');`], {
        type: 'application/javascript',
      })
    )
  );
};

/**
 * A mapping of monaco web worker labels to worker factories
 *
 * You might be tempted to extract/DRY the URL-building into `buildWorker`. Don't.
 * It leads to "Mixed Content" errors when loading the workers, and they're blocked
 */
const WORKER_MAP = {
  editorWorkerService: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/editor/editor.worker.js',
        import.meta.url
      )
    ),
  css: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/language/css/css.worker.js',
        import.meta.url
      )
    ),
  html: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/language/html/html.worker.js',
        import.meta.url
      )
    ),
  json: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/language/json/json.worker.js',
        import.meta.url
      )
    ),
  typescript: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/language/typescript/ts.worker.js',
        import.meta.url
      )
    ),
  javascript: () =>
    buildWorker(
      new URL(
        'npm:monaco-editor/esm/vs/language/typescript/ts.worker.js',
        import.meta.url
      )
    ),
};

export default function getMonacoWorker(label: string): Worker {
  if (!isWorkerKey(label)) {
    throw new Error(`Unknown monaco worker ${label}`);
  }
  const getWorker: (() => Worker) | void = WORKER_MAP[label];

  return getWorker();
}

const workerKeys = Object.keys(WORKER_MAP);
function isWorkerKey(key: string): key is keyof typeof WORKER_MAP {
  return workerKeys.includes(key);
}
